import { useEffect, useMemo, useRef, useState } from 'react';

import type { GitHubInstallationSummary } from '../../components/screens/RunScreens';
import type { LoadState } from '../../components/workflowShared';
import { runGetIssues, runIsReady, type RunDetailsResponse } from './appRunData';
import {
  runStatusFormatDuration,
  runStatusGetResultProcessingStatus,
  runStatusGetSkillProgressItems
} from '../../run/RunStatusPage';
import {
  isDevRunId,
  loadRepositoryChoices,
  loadRunDetails,
  isUploadedScanTarget,
  type AppPentestTarget,
  type AppRepositoryChoice
} from './appScanApi';
import {
  IconAlert,
  IconArchive,
  IconBranch,
  IconCheck,
  IconChevronDown,
  IconFile,
  IconGithub,
  IconIssues,
  IconLock,
  IconTag
} from './appIcons';
import { APP_PLANS, formatResetsIn, getAppPlanRank, type AppPlan } from './appPlans';
import type { AppGitHubState } from './useAppAccount';

const RUN_POLL_INTERVAL_MS = 3000;
const DEV_RUN_POLL_INTERVAL_MS = 1000;
const REPOSITORY_FILTER_THRESHOLD = 6;
const RUN_FINISHED_STATUSES = ['completed', 'partial'];
const STEP_DONE_STATUSES = ['completed', 'partial', 'skipped'];

const getErrorText = (error: unknown, fallback: string): string => {
  return error instanceof Error && error.message ? error.message : fallback;
};

function AppSpinner() {
  return <span className="app-spinner" aria-hidden={true} />;
}

/* ── Upload ZIP ─────────────────────────────────────────────────────────── */

/** Picks a codebase ZIP, for people who would rather not give Havu GitHub access. */
export function AppUploadZipButton({ onSelect }: { onSelect: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button type="button" className="app-action-button" onClick={() => inputRef.current?.click()}>
        <IconArchive aria-hidden={true} />
        Upload ZIP
      </button>
      <input
        ref={inputRef}
        className="app-upload-input"
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];

          event.target.value = '';

          if (file) {
            onSelect(file);
          }
        }}
      />
    </>
  );
}

/* ── Connect GitHub ─────────────────────────────────────────────────────── */

export function AppConnectGitHubWidget({
  githubState,
  onConnect,
  onManage,
  onUploadZip
}: {
  githubState: AppGitHubState;
  onConnect: () => void;
  onManage: () => void;
  /** Offers a ZIP upload next to GitHub as the alternative to repository access. */
  onUploadZip?: (file: File) => void;
}) {
  if (githubState === 'connected') {
    return (
      <p className="app-widget-done">
        <IconCheck aria-hidden={true} />
        GitHub connected
      </p>
    );
  }

  return (
    <div className="app-widget-actions">
      <button
        type="button"
        className="app-action-button"
        disabled={githubState === 'loading'}
        onClick={githubState === 'needs-install' ? onManage : onConnect}
      >
        <IconGithub aria-hidden={true} />
        {githubState === 'needs-install' ? 'Choose repositories' : 'Connect GitHub'}
      </button>
      {onUploadZip && <AppUploadZipButton onSelect={onUploadZip} />}
    </div>
  );
}

/* ── Repository picker ──────────────────────────────────────────────────── */

export function AppRepoPickerWidget({
  sessionId,
  installations,
  chosen,
  onPick
}: {
  sessionId: string;
  installations: GitHubInstallationSummary[];
  chosen?: string;
  onPick: (choice: AppRepositoryChoice) => Promise<void>;
}) {
  const [choices, setChoices] = useState<AppRepositoryChoice[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [filter, setFilter] = useState('');
  const [pickingId, setPickingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (chosen) {
      return undefined;
    }

    let cancelled = false;

    setLoadState('loading');
    loadRepositoryChoices(sessionId, installations)
      .then((loaded) => {
        if (!cancelled) {
          setChoices(loaded);
          setLoadState('ready');
        }
      })
      .catch((loadError: unknown) => {
        console.error('Failed to load repositories:', loadError);

        if (!cancelled) {
          setLoadState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chosen, installations, sessionId]);

  const visibleChoices = useMemo(() => {
    const query = filter.trim().toLowerCase();

    return query
      ? choices.filter((choice) => choice.repository.fullName.toLowerCase().includes(query))
      : choices;
  }, [choices, filter]);

  if (chosen) {
    return (
      <p className="app-widget-done">
        <IconCheck aria-hidden={true} />
        {chosen} · saved as workspace scope
      </p>
    );
  }

  if (loadState === 'loading') {
    return <div className="app-skeleton" role="status" aria-label="Loading repositories"><span /><span /></div>;
  }

  if (loadState === 'error') {
    return <p className="app-widget-error">Repositories could not be loaded.</p>;
  }

  if (choices.length === 0) {
    return <p className="app-widget-muted">No repositories are shared with Havu yet.</p>;
  }

  return (
    <div className="app-card app-repo-picker">
      {choices.length > REPOSITORY_FILTER_THRESHOLD && (
        <input
          className="app-card-filter"
          value={filter}
          placeholder="Search repositories"
          aria-label="Search repositories"
          onChange={(event) => setFilter(event.target.value)}
        />
      )}
      <div className="app-card-list">
        {visibleChoices.map((choice) => (
          <button
            key={`${choice.installationId}-${choice.repository.id}`}
            type="button"
            className="app-card-row"
            disabled={pickingId !== null}
            onClick={() => {
              setPickingId(choice.repository.id);
              setError('');
              onPick(choice).catch((pickError: unknown) => {
                setError(getErrorText(pickError, 'The repository could not be selected.'));
                setPickingId(null);
              });
            }}
          >
            <span className="app-card-row-icon">
              {pickingId === choice.repository.id
                ? <AppSpinner />
                : choice.repository.private ? <IconLock aria-hidden={true} /> : <IconGithub aria-hidden={true} />}
            </span>
            <span className="app-card-row-main">{choice.repository.fullName}</span>
            {choice.repository.defaultBranch && (
              <span className="app-card-row-meta">
                <IconBranch aria-hidden={true} />
                {choice.repository.defaultBranch}
              </span>
            )}
          </button>
        ))}
      </div>
      {error && <p className="app-widget-error">{error}</p>}
    </div>
  );
}

/* ── Run pentest ────────────────────────────────────────────────────────── */

const PERIOD_LABELS: Record<AppPlan['period'], string> = { week: 'this week', month: 'this renewal period' };

/** One run action for every plan, using the active custom workflow or the built-in default. */
export function AppRunPentestWidget({
  target,
  plan,
  scansLeft,
  resetsAt,
  isLaunched,
  hasActiveWorkflow,
  onRun,
  onEditScope,
  onEditWorkflow,
  onViewPlans
}: {
  target: AppPentestTarget;
  plan: AppPlan;
  scansLeft: number;
  resetsAt: number | null;
  isLaunched: boolean;
  hasActiveWorkflow: boolean;
  onRun: () => Promise<void>;
  onEditScope?: () => void;
  onEditWorkflow: () => void;
  onViewPlans: () => void;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState('');
  const isTopPlan = getAppPlanRank(plan.id) === APP_PLANS.length - 1;
  const period = plan.id === 'none' ? 'without a subscription' : PERIOD_LABELS[plan.period];
  const allowance = scansLeft > 0
    ? `${scansLeft} of ${plan.scansPerPeriod} ${plan.scansPerPeriod === 1 ? 'assessment' : 'assessments'} left ${period}`
    : `No assessments left ${period}${resetsAt === null ? '' : ` · refreshes ${formatResetsIn(resetsAt)}`}`;

  const run = () => {
    setIsRunning(true);
    setError('');
    onRun()
      .catch((runError: unknown) => setError(getErrorText(runError, 'The scan could not be started.')))
      .finally(() => setIsRunning(false));
  };

  return (
    <div className="app-card app-pricing">
      <div className="app-card-head">
        {isUploadedScanTarget(target) ? <IconFile aria-hidden={true} /> : <IconGithub aria-hidden={true} />}
        <span>{isUploadedScanTarget(target) ? target.fileName : target.fullName}</span>
        <span className="app-card-head-meta">
          {isUploadedScanTarget(target) ? (
            <>{target.sizeBytes < 1024 * 1024
              ? `${Math.max(1, Math.round(target.sizeBytes / 1024))} KB`
              : `${(target.sizeBytes / (1024 * 1024)).toFixed(1)} MB`} · ZIP archive</>
          ) : (
            <>
              <IconBranch aria-hidden={true} />
              {target.branch}
              {' · '}
              {target.scopePaths.length === 0
                ? 'Full codebase'
                : `${target.scopePaths.length} ${target.scopePaths.length === 1 ? 'folder' : 'folders'}`}
            </>
          )}
        </span>
        {onEditScope && !isLaunched && !isUploadedScanTarget(target) && (
          <button type="button" className="app-card-head-action" onClick={onEditScope}>Edit scope</button>
        )}
      </div>

      <div className="app-plan">
        <div className="app-plan-copy">
          <strong>Security assessment</strong>
          <span>
            Recon, exploit development and validation with{' '}
            {hasActiveWorkflow ? 'your ' : 'the '}
            <button type="button" className="app-plan-link" onClick={onEditWorkflow}>
              {hasActiveWorkflow ? 'active workflow' : 'default workflow'}
            </button>
          </span>
        </div>
        {isLaunched ? (
          <span className="app-plan-started">
            <IconCheck aria-hidden={true} />
            Started
          </span>
        ) : (
          <button
            type="button"
            className="app-plan-run primary"
            disabled={isRunning || scansLeft === 0}
            onClick={run}
          >
            {isRunning ? <AppSpinner /> : plan.id === 'none' ? 'Choose a plan' : 'Run'}
          </button>
        )}
      </div>

      <div className={`app-run-allowance${scansLeft === 0 ? ' spent' : ''}`}>
        <span>{allowance}</span>
        <button type="button" className="app-card-head-action" onClick={onViewPlans}>
          {isTopPlan ? 'View plans' : 'Upgrade'}
        </button>
      </div>

      {error && <p className="app-widget-error app-card-body">{error}</p>}
    </div>
  );
}

/* ── Plans ──────────────────────────────────────────────────────────────── */

export function AppPlansWidget({ onViewPlans }: { onViewPlans: () => void }) {
  return (
    <button type="button" className="app-action-button" onClick={onViewPlans}>
      <IconTag aria-hidden={true} />
      View plans
    </button>
  );
}

/* ── Pentest progress ───────────────────────────────────────────────────── */

type StepState = 'pending' | 'active' | 'done' | 'failed';

const getSkillStepState = (status: string, progress: number): StepState => {
  if (STEP_DONE_STATUSES.includes(status)) {
    return 'done';
  }

  if (status === 'failed') {
    return 'failed';
  }

  return status === 'running' || progress > 0 ? 'active' : 'pending';
};

function AppStepIcon({ state }: { state: StepState }) {
  return (
    <span className={`app-step-icon ${state}`} aria-hidden={true}>
      {state === 'active' && <AppSpinner />}
      {state === 'done' && <IconCheck aria-hidden={true} />}
      {state === 'failed' && <IconAlert aria-hidden={true} />}
    </span>
  );
}

/** Collapsible scan activity, in the style of a model's reasoning dropdown. */
export function AppPentestProgressWidget({
  runId,
  defaultOpen = false,
  newCount,
  isAutoWorkflow = false,
  onFinished,
  onViewIssues
}: {
  runId: string;
  defaultOpen?: boolean;
  /** The backend picked or generated the workflow for this run. */
  isAutoWorkflow?: boolean;
  /** Findings this scan added to the register, once the register has loaded it. */
  newCount?: number;
  onFinished?: () => void;
  onViewIssues?: () => void;
}) {
  const [details, setDetails] = useState<RunDetailsResponse | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [retryKey, setRetryKey] = useState(0);
  const onFinishedRef = useRef(onFinished);
  // Only a run seen in progress reports completion; reopening an old chat should not.
  const wasRunningRef = useRef(false);
  const isReady = runIsReady(details);

  onFinishedRef.current = onFinished;

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const interval = isDevRunId(runId) ? DEV_RUN_POLL_INTERVAL_MS : RUN_POLL_INTERVAL_MS;

    const poll = async () => {
      try {
        const response = await loadRunDetails(runId);

        if (cancelled) {
          return;
        }

        setDetails(response);
        setHasError(false);

        if (!runIsReady(response)) {
          wasRunningRef.current = true;
          timer = window.setTimeout(() => void poll(), interval);
        } else if (wasRunningRef.current) {
          wasRunningRef.current = false;
          onFinishedRef.current?.();
        }
      } catch (pollError) {
        console.error('Failed to load run progress:', pollError);

        if (!cancelled) {
          setHasError(true);
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [runId, retryKey]);

  useEffect(() => {
    if (isReady) {
      return undefined;
    }

    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [isReady]);

  const status = details?.status;
  const resultProcessing = runStatusGetResultProcessingStatus(status);
  const isFailed = Boolean(status?.setupError) || (isReady && !RUN_FINISHED_STATUSES.includes(status?.status ?? ''));
  const startedMs = status ? new Date(status.createdAt).getTime() : nowMs;
  const endedMs = isReady && status ? new Date(status.updatedAt).getTime() : nowMs;
  const duration = runStatusFormatDuration(Math.max(0, endedMs - startedMs));
  const issueCount = isReady ? runGetIssues(details).length : 0;
  const skillItems = status ? runStatusGetSkillProgressItems(status, nowMs) : [];
  const headerState: StepState = hasError || isFailed ? 'failed' : isReady ? 'done' : 'active';
  const title = hasError
    ? 'Assessment status unavailable'
    : !status
      ? 'Starting assessment'
      : isFailed ? 'Assessment failed' : isReady ? 'Assessment finished' : 'Running assessment';
  const preparationState: StepState = status?.setupError
    ? 'failed'
    : status && (status.agents.length > 0 || isReady) ? 'done' : 'active';
  const consolidationStatus = resultProcessing?.status ?? 'pending';
  const consolidationState: StepState = STEP_DONE_STATUSES.includes(consolidationStatus)
    ? 'done'
    : consolidationStatus === 'failed' ? 'failed' : consolidationStatus === 'running' ? 'active' : 'pending';

  return (
    <div className={`app-activity${isOpen ? ' open' : ''}`}>
      <button
        type="button"
        className="app-activity-toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <AppStepIcon state={headerState} />
        <span className={`app-activity-title${headerState === 'active' ? ' shimmer' : ''}`}>{title}</span>
        {isAutoWorkflow && <span className="app-status-pill" title="Workflow picked automatically">Auto</span>}
        {status && <span className="app-activity-meta">{duration}</span>}
        <IconChevronDown aria-hidden={true} className="app-activity-chevron" />
      </button>

      {isOpen && (
        <ol className="app-steps">
          <li className={`app-step ${preparationState}`}>
            <AppStepIcon state={preparationState} />
            <span className="app-step-label">Preparing repository</span>
          </li>
          {skillItems.map((item) => {
            const state = getSkillStepState(item.status, item.progress);

            return (
              <li key={item.skill} className={`app-step ${state}`}>
                <AppStepIcon state={state} />
                <span className="app-step-label">{item.label}</span>
                <span className="app-step-detail">{item.detail}</span>
              </li>
            );
          })}
          <li className={`app-step ${consolidationState}`}>
            <AppStepIcon state={consolidationState} />
            <span className="app-step-label">Consolidating findings</span>
          </li>
        </ol>
      )}

      {hasError && (
        <button type="button" className="app-text-button" onClick={() => setRetryKey((key) => key + 1)}>
          Retry
        </button>
      )}

      {isReady && !isFailed && (
        <div className="app-activity-result">
          <span>
            {issueCount === 0 ? 'No findings' : `${issueCount} ${issueCount === 1 ? 'finding' : 'findings'}`}
            {issueCount > 0 && newCount !== undefined && <span className="app-activity-new"> · {newCount} new</span>}
          </span>
          {issueCount > 0 && onViewIssues && (
            <button type="button" className="app-action-button" onClick={onViewIssues}>
              <IconIssues aria-hidden={true} />
              View in Issues
            </button>
          )}
        </div>
      )}
    </div>
  );
}
